import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useGamification } from '../hooks/GamificationContext';
import { useSingletonAudio } from '../hooks/useSingletonAudio';
import { useTreasureMapData } from '../hooks/useTreasureMapData';
import { useTreasureMapUi } from '../hooks/useTreasureMapUi';
import { getTreasureMapPermissions } from '../utils/treasureMapPermissions';
import {
  filterTreasureMapItems,
  sortTreasureMapItems,
  splitTreasureMapItemsBySource,
} from '../utils/treasureMapFilters';
import { buildTreasureMapItemKey } from '../utils/treasureMapNormalizer';
import {
  CATEGORY_ICONS,
  PFAND_NAEHE_M,
  PICKUP_MAX_ACCURACY_METERS,
} from '../trashcan/config/categoryIcons';
import { TrashcanMap } from './trashcan/TrashcanMap';
import { TreasureMapToolbar } from './treasure-map/TreasureMapToolbar';
import { TreasureMapFilterPanel } from './treasure-map/TreasureMapFilterPanel';
import { TreasureMapListView } from './treasure-map/TreasureMapListView';
import { TreasureMapDetailsPanel } from './treasure-map/TreasureMapDetailsPanel';
import { TreasureMapAddDrawer } from './treasure-map/TreasureMapAddDrawer';

const BASE_SOUND_URL = '/app/assets/sounds';
const xpGainSound = `${BASE_SOUND_URL}/xp-gain.mp3`;

function createInitialLocation() {
  return {
    title: '',
    desc: '',
    category: '',
    latitude: null,
    longitude: null,
  };
}

function buildHeaderMeta(items = []) {
  const locations = items.filter((item) => item?.sourceType === 'location').length;
  const donations = items.filter((item) => item?.sourceType === 'donation').length;
  const lootable = items.filter((item) => item?.lootableNow === true).length;
  const bottleMessages = items.filter((item) => item?.sourceType === 'bottle_message').length;

  return {
    locations,
    donations,
    lootable,
    bottleMessages,
  };
}

function roundCoordinate(value) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return null;
  }

  return Number(numericValue.toFixed(6));
}

function buildInitialViewportBbox(startCenter) {
  if (!Array.isArray(startCenter) || startCenter.length !== 2) {
    return null;
  }

  const latitude = Number(startCenter[0]);
  const longitude = Number(startCenter[1]);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return null;
  }

  return {
    south: Number((latitude - 0.025).toFixed(5)),
    west: Number((longitude - 0.04).toFixed(5)),
    north: Number((latitude + 0.025).toFixed(5)),
    east: Number((longitude + 0.04).toFixed(5)),
  };
}

function formatLocationAge(ageMs) {
  if (!Number.isFinite(Number(ageMs))) {
    return null;
  }

  const seconds = Math.max(0, Math.round(Number(ageMs) / 1000));
  if (seconds < 2) {
    return 'gerade eben';
  }
  if (seconds < 60) {
    return `vor ${seconds} s`;
  }

  const minutes = Math.floor(seconds / 60);
  return `vor ${minutes} min`;
}

function buildLocationStatusText({ status, accuracy, ageMs, error }) {
  const ageLabel = formatLocationAge(ageMs);
  const accuracyLabel = Number.isFinite(Number(accuracy)) ? `+/- ${Math.round(Number(accuracy))} m` : null;
  const parts = [accuracyLabel, ageLabel ? `${ageLabel} aktualisiert` : null].filter(Boolean);
  const details = parts.length ? ` - ${parts.join(' - ')}` : '';

  if (status === 'denied') {
    return {
      tone: 'error',
      label: 'Standort blockiert',
      text: error || 'Standortfreigabe verweigert. Abholungen brauchen eine frische Position.',
    };
  }

  if (status === 'unsupported') {
    return {
      tone: 'error',
      label: 'Standort nicht verfuegbar',
      text: 'Dieses Geraet meldet keine Browser-Position.',
    };
  }

  if (status === 'error') {
    return {
      tone: 'warning',
      label: 'Standortfehler',
      text: error || 'Standort konnte gerade nicht ermittelt werden.',
    };
  }

  if (status === 'stale') {
    return {
      tone: 'warning',
      label: 'Standort wird aufgefrischt',
      text: `Keine frische Position${ageLabel ? ` seit ${ageLabel.replace('vor ', '')}` : ''}. Die App fragt GPS automatisch neu an.`,
    };
  }

  if (status === 'poor') {
    return {
      tone: 'warning',
      label: 'Schwaches Standortsignal',
      text: `Position aktiv, aber ungenau${details}. Abholungen warten auf ein besseres Signal.`,
    };
  }

  if (status === 'searching') {
    return {
      tone: 'info',
      label: 'Standort wird gesucht',
      text: 'GPS wird gestartet. Die Karte bleibt nutzbar, bis die erste Position da ist.',
    };
  }

  if (Number.isFinite(Number(accuracy)) && Number(accuracy) > PICKUP_MAX_ACCURACY_METERS) {
    return {
      tone: 'warning',
      label: 'Standort ungenau',
      text: `Live-Position aktiv${details}. Fuer Abholungen braucht die App ein genaueres Signal.`,
    };
  }

  return {
    tone: 'success',
    label: 'Live-Standort aktiv',
    text: `Deine Position aktualisiert sich automatisch${details}.`,
  };
}

function LocationStatus({ status, accuracy, ageMs, error }) {
  const statusInfo = buildLocationStatusText({ status, accuracy, ageMs, error });

  return (
    <div className={`treasure-map-location-status is-${statusInfo.tone}`}>
      <span className="treasure-map-location-status__dot" aria-hidden="true" />
      <div>
        <strong>{statusInfo.label}</strong>
        <span>{statusInfo.text}</span>
      </div>
    </div>
  );
}

export function TrashcanView() {
  const { level, addXP, applyGamificationPayload } = useGamification();
  const playXpSound = useSingletonAudio(xpGainSound, 0.7);
  const canManageLocations = Boolean(window?.pfandpiratData?.canManageLocations);

  const permissions = getTreasureMapPermissions({
    level,
    canManageLocations,
  });

  const [newLocation, setNewLocation] = useState(createInitialLocation);
  const [viewportBbox, setViewportBbox] = useState(null);
  const [mobileOverlayOffsetY, setMobileOverlayOffsetY] = useState(0);
  const overlayTouchStartYRef = useRef(null);
  const overlayTouchDeltaYRef = useRef(0);
  const [isMobileViewport, setIsMobileViewport] = useState(() => {
    if (typeof window === 'undefined') {
      return false;
    }

    return window.innerWidth <= 1024;
  });

  const {
    ready,
    userPos,
    geolocationLocation,
    geolocationAccuracy,
    geolocationStatus,
    geolocationAgeMs,
    defaultPosition,
    loading,
    errors,
    treasureMapItems,
    categories,
    bottleMessages,
    busyBottleMessageId,
    openedBottleMessages,
    openBottleMessage,
    lootMessage,
    canLoot,
    getCooldownTime,
    formatCooldown,
    handleLoot,
    handlePfandAbgeholt,
    addLocation,
    lastViewport,
    locationCacheState,
  } = useTreasureMapData({
    addXP,
    applyGamificationPayload,
    playXpSound,
    pfandNaeheM: PFAND_NAEHE_M,
    viewportBbox,
  });

  const ui = useTreasureMapUi({
    canAddLocation: permissions.canAddLocation,
  });

  const startCenter = useMemo(() => {
    if (Array.isArray(userPos) && userPos.length === 2) return userPos;
    if (lastViewport?.center) return [lastViewport.center.latitude, lastViewport.center.longitude];
    return defaultPosition;
  }, [userPos, defaultPosition, lastViewport]);

  const allFilterableItems = useMemo(() => {
    return [...treasureMapItems, ...bottleMessages];
  }, [treasureMapItems, bottleMessages]);

  const filteredItems = useMemo(() => {
    return filterTreasureMapItems(allFilterableItems, ui.filters);
  }, [allFilterableItems, ui.filters]);

  const sortedItems = useMemo(() => sortTreasureMapItems(filteredItems, 'distance'), [filteredItems]);

  const visibleSources = useMemo(() => {
    return splitTreasureMapItemsBySource(sortedItems);
  }, [sortedItems]);

  const mapLocations = visibleSources.locations.length ? visibleSources.locations : [];
  const mapDonations = visibleSources.donations.length ? visibleSources.donations : [];
  const mapBottleMessages = visibleSources.bottleMessages.length ? visibleSources.bottleMessages : [];
  const headerMeta = useMemo(() => buildHeaderMeta(sortedItems), [sortedItems]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handleResize = () => {
      setIsMobileViewport(window.innerWidth <= 1024);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);


  useEffect(() => {
    if (!sortedItems.length) {
      ui.clearSelectedItem();
      return;
    }

    const hasSelection = sortedItems.some((item) => buildTreasureMapItemKey(item) === ui.selectedItemKey);
    if (hasSelection) {
      return;
    }

    if (isMobileViewport) {
      ui.clearSelectedItem();
      return;
    }

    const preferredItem = sortedItems.find((item) => item?.sourceType !== 'bottle_message') || sortedItems[0];
    ui.selectItem(preferredItem, { followMap: false });
  }, [sortedItems, ui, isMobileViewport]);

  useEffect(() => {
    if (!permissions.canAddLocation) {
      return;
    }

    if (!Array.isArray(userPos) || userPos.length !== 2) {
      return;
    }

    setNewLocation((previous) => {
      if (Number.isFinite(Number(previous?.latitude)) && Number.isFinite(Number(previous?.longitude))) {
        return previous;
      }

      return {
        ...(previous || createInitialLocation()),
        latitude: roundCoordinate(userPos[0]),
        longitude: roundCoordinate(userPos[1]),
      };
    });
  }, [permissions.canAddLocation, userPos]);

  const selectedItem = useMemo(() => {
    if (!ui.selectedItemKey) {
      return null;
    }

    return sortedItems.find((item) => buildTreasureMapItemKey(item) === ui.selectedItemKey) || null;
  }, [sortedItems, ui.selectedItemKey]);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined;
    }

    if (!isMobileViewport || !selectedItem) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isMobileViewport, selectedItem]);

  useEffect(() => {
    setMobileOverlayOffsetY(0);
    overlayTouchStartYRef.current = null;
    overlayTouchDeltaYRef.current = 0;
  }, [selectedItem, isMobileViewport]);

  useEffect(() => {
    if (!isMobileViewport || !selectedItem || typeof window === 'undefined') {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        ui.clearSelectedItem();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isMobileViewport, selectedItem, ui]);

  const updateDraftCoordinates = (latitude, longitude) => {
    setNewLocation((previous) => ({
      ...(previous || {}),
      latitude: roundCoordinate(latitude),
      longitude: roundCoordinate(longitude),
    }));
  };

  const handleUseCurrentLocation = () => {
    if (!Array.isArray(userPos) || userPos.length !== 2) {
      alert('Dein Standort ist gerade nicht verfügbar.');
      return;
    }

    updateDraftCoordinates(userPos[0], userPos[1]);
  };

  const handleMapPickLocation = ({ latitude, longitude }) => {
    updateDraftCoordinates(latitude, longitude);
    ui.stopMapPickMode();
    ui.openAddLocationDrawer();
  };

  const handleAddLocationSubmit = async (event) => {
    event.preventDefault();

    if (!permissions.canAddLocation) {
      alert(
        'Du darfst Orte nur ab Level 5 oder mit Administrator-, Redakteur- oder Mitarbeiterrechten hinzufügen.'
      );
      return;
    }

    const result = await addLocation(newLocation);
    if (result?.ok) {
      alert('Ort erfolgreich hinzugefügt!');
      setNewLocation({
        ...(result.resetState || createInitialLocation()),
        latitude: Array.isArray(userPos) && userPos.length === 2 ? roundCoordinate(userPos[0]) : null,
        longitude: Array.isArray(userPos) && userPos.length === 2 ? roundCoordinate(userPos[1]) : null,
      });
      ui.closeAddLocationDrawer();
    }
  };

  const hasDataError = Boolean(errors.locations || errors.donations);
  const hasGeoHint = Boolean(errors.geolocation && !userPos);
  const showMap = ui.viewMode === 'map';


  useEffect(() => {
    if (viewportBbox) {
      return;
    }

    const initialViewport = buildInitialViewportBbox(startCenter);
    if (!initialViewport) {
      return;
    }

    setViewportBbox(initialViewport);
  }, [startCenter, viewportBbox]);

  const handleViewportChange = useCallback((nextViewportBbox) => {
    setViewportBbox((previous) => {
      const nextKey = nextViewportBbox
        ? `${nextViewportBbox.south}|${nextViewportBbox.west}|${nextViewportBbox.north}|${nextViewportBbox.east}`
        : '';
      const previousKey = previous
        ? `${previous.south}|${previous.west}|${previous.north}|${previous.east}`
        : '';

      return nextKey === previousKey ? previous : nextViewportBbox;
    });
  }, []);

  const closeMobileOverlay = useCallback(() => {
    setMobileOverlayOffsetY(0);
    overlayTouchStartYRef.current = null;
    overlayTouchDeltaYRef.current = 0;
    ui.clearSelectedItem();
  }, [ui]);

  const handleMobileOverlayTouchStart = useCallback((event) => {
    if (!isMobileViewport) {
      return;
    }

    const touch = event.touches?.[0];
    if (!touch) {
      return;
    }

    overlayTouchStartYRef.current = touch.clientY;
    overlayTouchDeltaYRef.current = 0;
  }, [isMobileViewport]);

  const handleMobileOverlayTouchMove = useCallback((event) => {
    if (!isMobileViewport || overlayTouchStartYRef.current === null) {
      return;
    }

    const touch = event.touches?.[0];
    if (!touch) {
      return;
    }

    const deltaY = Math.max(0, touch.clientY - overlayTouchStartYRef.current);
    overlayTouchDeltaYRef.current = deltaY;
    setMobileOverlayOffsetY(deltaY);
  }, [isMobileViewport]);

  const handleMobileOverlayTouchEnd = useCallback(() => {
    if (!isMobileViewport) {
      return;
    }

    const deltaY = overlayTouchDeltaYRef.current;
    overlayTouchStartYRef.current = null;
    overlayTouchDeltaYRef.current = 0;

    if (deltaY >= 120) {
      closeMobileOverlay();
      return;
    }

    setMobileOverlayOffsetY(0);
  }, [closeMobileOverlay, isMobileViewport]);

  return (
    <div className="treasure-map-page" style={{ maxWidth: 1120, margin: '1.5em auto' }}>
      <div className="treasure-map-page__header">
        <h2 style={{ margin: 0 }}>🗺️ SchatzKarte</h2>
        <div className="treasure-map-page__header-meta">
          Sichtbar: <strong>{sortedItems.length}</strong> Punkte · Orte: <strong>{headerMeta.locations}</strong> · Pfandspenden:{' '}
          <strong>{headerMeta.donations}</strong> · Lootbar: <strong>{headerMeta.lootable}</strong> · Flaschenpost:{' '}
          <strong>{headerMeta.bottleMessages}</strong>
          {showMap ? <span className="treasure-map-page__viewport-note"> · Kartenpunkte werden für den aktuellen Ausschnitt nachgeladen</span> : null}
        </div>
      </div>

      <TreasureMapToolbar
        visibleCount={sortedItems.length}
        categories={categories}
        filters={ui.filters}
        viewMode={ui.viewMode}
        activeFilterCount={ui.activeFilterCount}
        canAddLocation={permissions.canAddLocation}
        onQueryChange={ui.setQuery}
        onCategoryChange={ui.setActiveCategory}
        onToggleNearby={ui.toggleNearbyOnly}
        onToggleLootable={ui.toggleLootableOnly}
        onToggleDonationsOnly={ui.toggleDonationsOnly}
        onViewModeChange={ui.setViewMode}
        onToggleFilterPanel={() => ui.setShowFilterPanel((previous) => !previous)}
        onToggleAddLocationForm={ui.openAddLocationDrawer}
      />

      {ui.showFilterPanel && (
        <TreasureMapFilterPanel
          filters={ui.filters}
          categories={categories}
          onToggleLayer={ui.toggleLayer}
          onSetMaxDistanceMeters={ui.setMaxDistanceMeters}
          onApplyQuickFilter={ui.applyQuickFilter}
          onResetFilters={ui.resetFilters}
        />
      )}

      {hasDataError && (
        <div className="treasure-map-alert treasure-map-alert--error">
          {errors.locations || errors.donations}
        </div>
      )}

      {locationCacheState?.veryStale && mapLocations.length > 0 && (
        <div className="treasure-map-alert treasure-map-alert--info">
          Letzter bekannter Ortsstand – die Aktualisierung läuft im Hintergrund.
        </div>
      )}

      {errors.pfandAbgeholt && (
        <div className="treasure-map-alert treasure-map-alert--error">
          {errors.pfandAbgeholt}
        </div>
      )}

      {errors.addLocation && (
        <div className="treasure-map-alert treasure-map-alert--error">
          {errors.addLocation}
        </div>
      )}

      {hasGeoHint && (
        <div className="treasure-map-alert treasure-map-alert--info">
          {geolocationStatus === 'denied'
            ? 'Standortfreigabe verweigert. Die Karte bleibt nutzbar, Abholungen brauchen aber deinen aktuellen Standort.'
            : 'Dein Standort konnte gerade nicht ermittelt werden. Die Karte startet mit Dresden als Fallback und Funktionen mit Distanzprüfung sind eventuell eingeschränkt.'}
        </div>
      )}

      <LocationStatus
        status={geolocationStatus}
        accuracy={geolocationAccuracy}
        ageMs={geolocationAgeMs}
        error={errors.geolocation}
      />

      {!permissions.canAddLocation && (
        <div className="treasure-map-level-hint">
          <div>
            <strong>Eigene Orte hinzufügen</strong>
            <div>Diese Funktion wird ab Level 5 oder mit WordPress-Rechten für Administrator, Redakteur oder Mitarbeiter freigeschaltet.</div>
          </div>
        </div>
      )}

      <div className="treasure-map-content-layout">
        <div className="treasure-map-content-layout__main">
          {showMap ? (
            <TrashcanMap
              ready={ready}
              startCenter={startCenter}
              userPos={userPos}
              userLocation={geolocationLocation}
              pfandSpenden={mapDonations}
              allLocations={mapLocations}
              bottleMessages={mapBottleMessages}
              selectedItem={selectedItem}
              selectedItemKey={ui.selectedItemKey}
              shouldFollowSelectedItem={ui.shouldFollowSelectedItem}
              onSelectItem={ui.selectItem}
              onViewportChange={handleViewportChange}
              categoryIcons={CATEGORY_ICONS}
              isMapPickMode={ui.mapPickMode}
              onPickLocation={handleMapPickLocation}
              draftLocation={newLocation}
              showDraftLocationPreview={permissions.canAddLocation && ui.showAddLocationDrawer}
            />
          ) : (
            <TreasureMapListView
              items={sortedItems}
              selectedItemKey={ui.selectedItemKey}
              onSelectItem={ui.selectItem}
            />
          )}
        </div>

        <div className="treasure-map-content-layout__details">
          <TreasureMapDetailsPanel
            item={selectedItem}
            selectedItemKey={ui.selectedItemKey}
            canLoot={canLoot}
            getCooldownTime={getCooldownTime}
            formatCooldown={formatCooldown}
            handleLoot={handleLoot}
            handlePfandAbgeholt={handlePfandAbgeholt}
            busyBottleMessageId={busyBottleMessageId}
            openedBottleMessages={openedBottleMessages}
            onOpenBottleMessage={openBottleMessage}
            lootMessage={lootMessage}
          />
        </div>
      </div>

      {isMobileViewport && selectedItem && typeof document !== 'undefined' && ReactDOM.createPortal(
        <div className="treasure-map-mobile-overlay" role="dialog" aria-modal="true" aria-label="Details zum ausgewählten Punkt">
          <button
            type="button"
            className="treasure-map-mobile-overlay__backdrop"
            aria-label="Details schließen"
            onClick={closeMobileOverlay}
          />
          <div
            className="treasure-map-mobile-overlay__sheet"
            style={{ transform: `translateY(${mobileOverlayOffsetY}px)` }}
            onTouchStart={handleMobileOverlayTouchStart}
            onTouchMove={handleMobileOverlayTouchMove}
            onTouchEnd={handleMobileOverlayTouchEnd}
          >
            <TreasureMapDetailsPanel
              item={selectedItem}
              selectedItemKey={ui.selectedItemKey}
              canLoot={canLoot}
              getCooldownTime={getCooldownTime}
              formatCooldown={formatCooldown}
              handleLoot={handleLoot}
              handlePfandAbgeholt={handlePfandAbgeholt}
              busyBottleMessageId={busyBottleMessageId}
              openedBottleMessages={openedBottleMessages}
              onOpenBottleMessage={openBottleMessage}
              lootMessage={lootMessage}
              variant="overlay"
              onClose={closeMobileOverlay}
            />
          </div>
        </div>,
        document.body
      )}

      <TreasureMapAddDrawer
        isOpen={permissions.canAddLocation && ui.showAddLocationDrawer}
        level={level}
        canManageLocations={permissions.canManageLocations}
        newLocation={newLocation}
        setNewLocation={setNewLocation}
        categories={categories}
        onSubmit={handleAddLocationSubmit}
        onClose={ui.closeAddLocationDrawer}
        onUseCurrentLocation={handleUseCurrentLocation}
        onToggleMapPick={ui.toggleMapPickMode}
        isMapPickMode={ui.mapPickMode}
        userPos={userPos}
        isSubmitting={loading.addLocation}
      />

      {(loading.locations || loading.donations || loading.addLocation) && (
        <div className="treasure-map-loading-hint">
          Schatzkarten-Daten werden aktualisiert …
        </div>
      )}
    </div>
  );
}

export default TrashcanView;
